import { usePrefersReducedMotion } from '../../../hooks/usePrefersReducedMotion';

export interface MarqueeLogo {
  src: string;
  alt: string;
  height: number;
}

interface LogoMarqueeProps {
  logos: MarqueeLogo[];
  /** Seconds for one full loop. Larger = slower. */
  speed?: number;
  /** When true, logos load eagerly (use in the hero — lazy loading delays LCP). */
  priority?: boolean;
}

const GAP = 48; // px between logos
const VIEWPORT_WIDTH = 420; // px — sized so ~3 logos show inside the fade-masked window

function LogoImg({ logo, hidden, priority }: { logo: MarqueeLogo; hidden?: boolean; priority?: boolean }) {
  return (
    <img
      src={logo.src}
      alt={hidden ? '' : logo.alt}
      aria-hidden={hidden ? 'true' : undefined}
      loading={priority ? 'eager' : 'lazy'}
      decoding={priority ? 'sync' : 'async'}
      style={{ height: `${logo.height}px`, width: 'auto', flexShrink: 0, opacity: 0.9 }}
    />
  );
}

/**
 * Continuous horizontal logo marquee. Roughly three logos are visible at a time
 * inside a fade-masked viewport; the track scrolls left in a seamless infinite
 * loop. Honours `prefers-reduced-motion` by falling back to a static wrapped row.
 *
 * Note: the loop is an intentionally continuous animation (not the project's
 * usual ≤500ms cap) — an explicit, approved exception for the "trusted by" strip.
 */
export function LogoMarquee({ logos, speed = 28, priority = false }: LogoMarqueeProps) {
  const reducedMotion = usePrefersReducedMotion();

  if (reducedMotion) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: `${GAP}px`,
          flexWrap: 'wrap',
          marginTop: '14px',
        }}
      >
        {logos.map((logo) => (
          <LogoImg key={logo.alt} logo={logo} priority={priority} />
        ))}
      </div>
    );
  }

  // Mask fades both edges so logos appear/disappear softly (~3 visible window).
  const fadeMask =
    'linear-gradient(to right, transparent 0, #000 12%, #000 88%, transparent 100%)';

  return (
    <div
      className="logo-marquee"
      style={{
        marginTop: '14px',
        maxWidth: `${VIEWPORT_WIDTH}px`,
        overflow: 'hidden',
        WebkitMaskImage: fadeMask,
        maskImage: fadeMask,
      }}
    >
      <div
        className="logo-marquee-track"
        style={{ display: 'flex', alignItems: 'center', width: 'max-content', animationDuration: `${speed}s` }}
      >
        {/* Two identical sequences so translateX(-50%) loops seamlessly. */}
        {[0, 1].map((seq) => (
          <div
            key={seq}
            style={{ display: 'flex', alignItems: 'center', gap: `${GAP}px`, paddingRight: `${GAP}px` }}
          >
            {logos.map((logo) => (
              <LogoImg key={`${seq}-${logo.alt}`} logo={logo} hidden={seq === 1} priority={priority} />
            ))}
          </div>
        ))}
      </div>
      <style>{`
        @keyframes logo-marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .logo-marquee-track {
          animation-name: logo-marquee;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }
        .logo-marquee:hover .logo-marquee-track { animation-play-state: paused; }
      `}</style>
    </div>
  );
}
